/**
 * RadioSettings — the site's RF configuration (Wireless > Radio Settings).
 *
 * Site-scoped: per band (2.4/5/6 GHz) an operator sets channel (auto or fixed),
 * channel width, and TX power. Saving fans out to every AP in the site via owgw,
 * audited. Channel "auto" is the RRM default and the sensible site-wide choice;
 * a fixed channel applies to all APs (fine for a single-AP site).
 *
 * Per-AP overrides (OP#1760): an AP can keep its own channel / width / TX power (device detail >
 * Radios). A save here never overwrites those -- each card says how many APs have their own
 * setting, and the save result names the ones it kept. 2.4 GHz width is 20/40 only (20
 * recommended; it is also what the controller renders when nothing sets a 2.4 GHz width).
 */
import { useState } from "react";
import { useSession } from "@/stores/session";
import { useRadios, useUpdateRadio } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import type { Radio } from "@/types/domain";
import { RECOMMENDED_2G_WIDTH, SITE_WIDTHS, keptOverridesText } from "@/lib/radioOverrides";

const BAND_LABEL: Record<string, string> = { "2G": "2.4 GHz", "5G": "5 GHz", "6G": "6 GHz" };
// Widths valid per band. 2.4 GHz is 20/40 only (the server refuses more); see lib/radioOverrides.
const WIDTHS = SITE_WIDTHS;
// Multicast base rate (schema units; value/1000 = Mbps). Raising it clears airtime on
// dense networks by dropping weak clients from multicast. 24 Mbps is the common fix.
const MCAST_RATES = [6000, 12000, 18000, 24000, 36000, 48000, 54000];

export default function RadioSettings() {
  const siteId = useSession((s) => s.currentSiteId);
  const siteName = useSession((s) => s.currentSite()?.name);
  const { data, isLoading, error } = useRadios(siteId);

  if (!siteId) {
    return (
      <div>
        <PageHeader title="Radio Settings" subtitle="RF configuration" />
        <EmptyState title="Select a site first" hint="Radio settings apply to every AP in a site. Pick a site from the scope bar above." />
      </div>
    );
  }

  const radios = data?.radios ?? [];

  return (
    <div>
      <PageHeader title="Radio Settings" subtitle={siteName ? `RF in ${siteName}` : undefined} />
      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading radios…</div>}
      {data && radios.length === 0 && (
        <EmptyState title="No radios" hint="No AP in this site reports radios from the gateway." />
      )}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {radios.map((r) => (
          <RadioCard key={r.band} siteId={siteId} radio={r} />
        ))}
      </div>
    </div>
  );
}

function RadioCard({ siteId, radio }: { siteId: string; radio: Radio }) {
  const update = useUpdateRadio(siteId);
  const [channel, setChannel] = useState(radio.channel === "auto" ? "" : String(radio.channel));
  const [autoChannel, setAutoChannel] = useState(radio.channel === "auto");
  const [width, setWidth] = useState(radio.channel_width != null ? String(radio.channel_width) : "");
  const [power, setPower] = useState(radio.tx_power === "auto" ? "" : String(radio.tx_power));
  const [autoPower, setAutoPower] = useState(radio.tx_power === "auto");
  const [mcast, setMcast] = useState(radio.multicast_rate != null ? String(radio.multicast_rate) : "");
  // Channel plan (Ruckus AP-group parity): which channels auto-select may pick from + DFS.
  const [allowedChannels, setAllowedChannels] = useState(
    radio.allowed_channels?.length ? radio.allowed_channels.join(",") : "");
  const [allowDfs, setAllowDfs] = useState(radio.allow_dfs ?? false);

  const save = () => {
    update.mutate({
      band: radio.band,
      channel: autoChannel || channel.trim() === "" ? "auto" : Number(channel),
      ...(width.trim() ? { channel_width: Number(width) } : {}),
      tx_power: autoPower || power.trim() === "" ? "auto" : Number(power),
      ...(mcast.trim() ? { multicast_rate: Number(mcast) } : {}),
      // The plan only applies to auto-channel; send it together (list + DFS) when set.
      ...(autoChannel && allowedChannels.trim()
        ? { allowed_channels: parseChannels(allowedChannels), allow_dfs: allowDfs }
        : {}),
    });
  };

  const widths = WIDTHS[radio.band] ?? [20, 40, 80, 160];
  const overridden = radio.overridden_aps ?? [];

  return (
    <section className="md-card p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <span className="font-medium text-ink">{BAND_LABEL[radio.band] ?? radio.band}</span>
        <span className="text-xs text-ink-subtle">{radio.channel_mode ?? ""}</span>
      </div>

      <div className="flex flex-col gap-3">
        {/* Channel */}
        <div className="flex flex-col gap-1 text-sm">
          <span className="text-ink-muted">Channel</span>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={autoChannel} onChange={(e) => setAutoChannel(e.target.checked)} />
            <span className="text-sm">Auto (RRM)</span>
          </label>
          {!autoChannel && (
            <input
              inputMode="numeric"
              value={channel}
              onChange={(e) => setChannel(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="e.g. 36"
              className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
            />
          )}
          {/* Channel plan: constrain which channels RRM may choose (AP-group parity). */}
          {autoChannel && (
            <div className="mt-1 flex flex-col gap-1.5 rounded border border-line bg-surface-2 p-2">
              <span className="text-xs text-ink-subtle">Channel plan (which channels auto may use)</span>
              <input
                value={allowedChannels}
                onChange={(e) => setAllowedChannels(e.target.value.replace(/[^0-9,\- ]/g, ""))}
                placeholder="e.g. 36,40,44,48,149,153,157,161 (blank = all)"
                className="rounded border border-line bg-surface px-3 py-2 font-mono text-xs text-ink outline-none focus:border-brand"
              />
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={allowDfs} onChange={(e) => setAllowDfs(e.target.checked)} />
                <span>Allow DFS channels</span>
              </label>
            </div>
          )}
        </div>

        {/* Width */}
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-ink-muted">Channel width</span>
          <select
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
          >
            <option value="">(unchanged)</option>
            {widths.map((w) => (
              <option key={w} value={w}>
                {w} MHz{radio.band === "2G" && w === RECOMMENDED_2G_WIDTH ? " (recommended)" : ""}
              </option>
            ))}
          </select>
          {radio.band === "2G" && (
            <span className="text-[11px] text-ink-subtle">
              20 MHz recommended. When no width is set the controller renders 20 MHz on each AP&apos;s
              next push (instead of the firmware&apos;s 80 → 40 MHz fallback).
            </span>
          )}
        </label>

        {/* TX power */}
        <div className="flex flex-col gap-1 text-sm">
          <span className="text-ink-muted">TX power</span>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={autoPower} onChange={(e) => setAutoPower(e.target.checked)} />
            <span className="text-sm">Auto</span>
          </label>
          {!autoPower && (
            <input
              inputMode="numeric"
              value={power}
              onChange={(e) => setPower(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="dBm (1-30)"
              className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand"
            />
          )}
        </div>

        {/* Multicast base rate (airtime fix) */}
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-ink-muted">Multicast rate</span>
          <select
            value={mcast}
            onChange={(e) => setMcast(e.target.value)}
            className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand"
          >
            <option value="">(unchanged)</option>
            {MCAST_RATES.map((r) => (
              <option key={r} value={r}>
                {r / 1000} Mbps{r === 24000 ? " (recommended)" : ""}
              </option>
            ))}
          </select>
        </label>

        {/* Per-AP overrides on this band: a site save keeps them (OP#1760). */}
        {overridden.length > 0 && (
          <div className="rounded border border-line bg-surface-2 p-2 text-[11px] text-ink-muted">
            <span className="font-medium text-ink">{keptOverridesText(overridden.length)}</span>
            {": "}
            {overridden.map((a) => `${a.name} (${a.fields.map(fieldLabel).join(", ")})`).join("; ")}
          </div>
        )}

        <RoleGate min="site_operator" mode="hide" fallback={<span className="text-xs text-ink-subtle">read-only</span>}>
          <div className="flex items-center gap-2">
            <Button variant="primary" disabled={update.isPending} onClick={save}>
              {update.isPending ? "Applying…" : "Apply"}
            </Button>
            {update.data && <span className="text-xs text-healthy">Pushed to {update.data.pushed} AP{update.data.pushed === 1 ? "" : "s"}.</span>}
            {update.data?.pending_confirmation?.length ? (
              <span className="text-xs text-info" title={update.data.pending_confirmation.map((p) => p.name).join(", ")}>
                Waiting for {update.data.pending_confirmation.length} AP
                {update.data.pending_confirmation.length === 1 ? "" : "s"} to confirm.
              </span>
            ) : null}
            {update.data?.kept_overrides?.length ? (
              <span className="text-xs text-ink-muted">{keptOverridesText(update.data.kept_overrides.length)}.</span>
            ) : null}
            {update.isError && <span className="text-xs text-critical">Failed.</span>}
          </div>
        </RoleGate>
      </div>
    </section>
  );
}

/** Parse a "36,40,149 153" channel list into a deduped, sorted number[] (invalid tokens dropped). */
function parseChannels(csv: string): number[] {
  const out = new Set<number>();
  for (const tok of csv.split(/[\s,]+/)) {
    const n = Number(tok.trim());
    if (Number.isInteger(n) && n >= 1 && n <= 233) out.add(n);
  }
  return [...out].sort((a, b) => a - b);
}

/** "channel_width" -> "width" for the overridden-AP list. */
function fieldLabel(f: string): string {
  return f === "channel_width" ? "width" : f === "tx_power" ? "TX power" : f;
}
